import { forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { Button } from 'ui/button';

import { isDefined } from 'utils/tsHelpers';
import { SelectValueDisplayProps, SelectValueType } from 'ui/selects';
import { DYNAMIC_CONTENT_INPUT_VARIANTS } from '../../../constants';

type TriggerProps = Pick<
  SelectValueDisplayProps<SelectValueType>,
  | 'selectedOptions'
  | 'toggleDropdown'
  | 'placeholder'
  | 'suffixUsed'
  | 'isDisabled'
  | 'isError'
  | 'isOpen'
>;

export const TriggerButton = styled(Button)<{
  isDisabled: boolean;
  isError: boolean;
  isOpen: boolean;
}>`
  width: fit-content;
  &&& {
    box-shadow: none;
    ${({ theme }) => `
    &:hover {
      border-color: ${theme.colors.primaryColors.purple50};
    }`}
    ${DYNAMIC_CONTENT_INPUT_VARIANTS.DEFAULT}
    ${({ isOpen }) => isOpen && DYNAMIC_CONTENT_INPUT_VARIANTS.OPEN}
    ${({ isDisabled }) => isDisabled && DYNAMIC_CONTENT_INPUT_VARIANTS.DISABLED}
    ${({ isError }) => isError && DYNAMIC_CONTENT_INPUT_VARIANTS.ERROR}
  }
`;

export const StyledText = styled.span<{ $isDisabled: boolean; $isError: boolean }>`
  ${({ theme }) => theme.styles.body.body2};
  color: inherit;
`;

export const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  (
    { toggleDropdown, selectedOptions = [], placeholder, suffixUsed, isDisabled, isError, isOpen },
    ref,
  ) => {
    const { Suffix, label } = selectedOptions[0] ?? {};
    // eslint-disable-line
    const content = useMemo(() => {
      const value = suffixUsed && isDefined(Suffix) ? Suffix : label;
      return value ?? placeholder;
    }, [selectedOptions]);

    return (
      <>
        <TriggerButton
          ref={ref}
          onClick={() => toggleDropdown()}
          isDisabled={isDisabled}
          isError={isError}
          isOpen={isOpen}
        >
          <StyledText $isDisabled={isDisabled} $isError={isError}>
            {content}
          </StyledText>
        </TriggerButton>
      </>
    );
  },
);
